import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  allowedChars,
  BETRIEBSSTELLE_MAX_LENGTH,
  BETRIEBSSTELLE_MIN_LENGTH,
  BETRIEBSSTELLE_REGEXP,
  DEFAULT_INPUT_MAX_LENGTH,
  DEFAULT_INPUT_MIN_LENGTH,
  DEFAULT_INPUT_REGEXP,
  Gleisart,
  Ort,
  Wegpunkt
} from '@repo/ui'

export function useStandortForm() {
  const { t } = useTranslation('standorteingabe')

  const baseSchema = z.object({
    ort: z.nativeEnum(Ort, {
      error: t('errors.ort.required')
    }),
    wegpunkt: z.nativeEnum(Wegpunkt, {
      error: t('errors.wegpunkt.required')
    })
  })

  const ortSchema = z.discriminatedUnion('ort', [
    z.object({
      ort: z.literal(Ort.Strecke),
      von: z
        .string()
        .min(BETRIEBSSTELLE_MIN_LENGTH, { message: t('errors.von.required') })
        .max(BETRIEBSSTELLE_MAX_LENGTH)
        .superRefine(allowedChars(BETRIEBSSTELLE_REGEXP)),
      nach: z
        .string()
        .min(BETRIEBSSTELLE_MIN_LENGTH, { message: t('errors.nach.required') })
        .max(BETRIEBSSTELLE_MAX_LENGTH)
        .superRefine(allowedChars(BETRIEBSSTELLE_REGEXP)),
      gleisart: z.nativeEnum(Gleisart, {
        error: t('errors.gleisart.required')
      })
    }),
    z.object({
      ort: z.literal(Ort.Bahnhof),
      gleis: z
        .string()
        .min(DEFAULT_INPUT_MIN_LENGTH, { message: t('errors.gleis.required') })
        .max(DEFAULT_INPUT_MAX_LENGTH)
        .superRefine(allowedChars(DEFAULT_INPUT_REGEXP)),
      betriebsstelle: z
        .string()
        .min(BETRIEBSSTELLE_MIN_LENGTH, {
          message: t('errors.betriebsstelle.required')
        })
        .max(BETRIEBSSTELLE_MAX_LENGTH)
        .superRefine(allowedChars(BETRIEBSSTELLE_REGEXP))
    })
  ])

  const wegpunktSchema = z.discriminatedUnion('wegpunkt', [
    z.object({
      wegpunkt: z.literal(Wegpunkt.Kilometrierung),
      kilometrierung: z
        .string()
        .min(DEFAULT_INPUT_MIN_LENGTH, {
          message: t('errors.kilometrierung.required')
        })
        .max(DEFAULT_INPUT_MAX_LENGTH)
        .superRefine(allowedChars(DEFAULT_INPUT_REGEXP))
    }),
    z.object({
      wegpunkt: z.literal(Wegpunkt.Weiche),
      weiche: z
        .string()
        .min(DEFAULT_INPUT_MIN_LENGTH, { message: t('errors.weiche.required') })
        .max(DEFAULT_INPUT_MAX_LENGTH)
        .superRefine(allowedChars(DEFAULT_INPUT_REGEXP))
    }),
    z.object({
      wegpunkt: z.literal(Wegpunkt.Signal),
      signal: z
        .string()
        .min(DEFAULT_INPUT_MIN_LENGTH, { message: t('errors.signal.required') })
        .max(DEFAULT_INPUT_MAX_LENGTH)
        .superRefine(allowedChars(DEFAULT_INPUT_REGEXP))
    })
  ])

  const formDataSchema = baseSchema.and(ortSchema).and(wegpunktSchema)

  type FormData = z.infer<typeof formDataSchema>

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    shouldUnregister: true
  })

  return { register, watch, handleSubmit, errors, formDataSchema }
}
